import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest, map, of, take } from 'rxjs';
import { Roles } from '../constants/enums/roles.enum';
import { AuthorisationService } from '../services/authorisation.service';

/** Route data read by {@link roleGuard}: the user needs at least one of these roles. */
export interface RoleGuardData {
  roles: Roles[];
}

export const NO_ACCESS_ROUTE = 'no-access';

/**
 * Lets the user through when they have at least one of the roles in the route's `data.roles`,
 * and sends them to the "no access" page otherwise. Runs after Auth0's AuthGuard, so the user is signed in.
 */
export const roleGuard: CanActivateFn = (route) => {
  const roles = (route.data as Partial<RoleGuardData>)?.roles ?? [];
  if (roles.length === 0) {
    return of(true);
  }

  const authorisationService = inject(AuthorisationService);
  const router = inject(Router);

  return combineLatest(roles.map((role) => authorisationService.isInRole(role))).pipe(
    take(1),
    map((matches) => matches.some((match) => match) || router.createUrlTree([NO_ACCESS_ROUTE]))
  );
};
